import { useState, useEffect, useRef } from "react";

// ─── SCORING ENGINE (unchanged) ───────────────────────────────────────────────
const PROCEDURE_VALUES = { "under1k":750,"1k3k":2000,"3k6k":4500,"6k10k":8000,"over10k":12000 };
const CONSULT_COUNTS   = { "under5":3,"5to10":7,"11to20":15,"21to40":30,"over40":45 };
const BOOKING_RATES    = { "over70":0.75,"50to70":0.60,"30to50":0.40,"under30":0.25,"donttrack":0.35 };
const RESPONSE_LEAK    = { "automated":0.03,"within1hr":0.10,"sameday":0.25,"nextday":0.45,"noconsistent":0.60 };
const MISSED_CALL_LEAK = { "autotext":0.02,"within1hr":0.10,"whenwecan":0.25,"voicemail":0.40,"notsure":0.40 };
const FOLLOWUP_LEAK    = { "automated":0.05,"manual2x":0.15,"ifweremember":0.35,"ballisincourt":0.55,"notsure":0.55 };
const POSTCONSULT_MISS = { "automated":0.08,"manualdays":0.20,"ifwehavetime":0.40,"waitforthem":0.60,"nothing":0.72 };
const NOSHOW_RATES     = { "multitouch":0.03,"singleauto":0.08,"manualconfirm":0.14,"relyonpatient":0.22,"noreminders":0.28 };
const TASK_HOURS       = { confirmcalls:4,routineqa:5,reviewrequests:2,consultfollowup:5,preopinstructions:3,pastpatients:3,leadtracking:4 };
const REACTIVATION_REV = { "automated":0,"occasional":800,"promotions":1200,"dontreach":2000,"nosystem":2400 };
const REVIEW_LEAKAGE   = { "under25":2800,"25to75":1600,"75to150":800,"150to300":300,"over300":0 };

function calcScores(a) {
  const apv=PROCEDURE_VALUES[a.q3]||4500, cons=CONSULT_COUNTS[a.q2]||15, brate=BOOKING_RATES[a.q7]||0.40;
  const estLeads=cons*2.2;
  const blendedLeak=((MISSED_CALL_LEAK[a.q4]||0.25)*0.35)+((RESPONSE_LEAK[a.q5]||0.25)*0.40)+((FOLLOWUP_LEAK[a.q6]||0.35)*0.25);
  const deadLeadLeakage=Math.round(estLeads*blendedLeak*brate*apv);
  const postconsultLeakage=Math.round(cons*(1-brate)*(POSTCONSULT_MISS[a.q8]||0.40)*apv);
  const noshowLeakage=Math.round(cons*(NOSHOW_RATES[a.q9]||0.14)*brate*apv);
  const repLeakage=REVIEW_LEAKAGE[a.q12_count]||1600;
  const reactLeakage=REACTIVATION_REV[a.q13]||1200;
  const totalRevenue=deadLeadLeakage+postconsultLeakage+noshowLeakage+repLeakage+reactLeakage;
  const taskHours=(a.q10||[]).reduce((s,t)=>s+(TASK_HOURS[t]||0),0);
  const reportedHrs={"under2":6,"2to5":13,"5to10":30,"10to20":60,"over20":90}[a.q11]||13;
  const blendedHours=Math.round(taskHours*0.6+reportedHrs*0.4);
  const timeDollarValue=Math.round(blendedHours*35);
  const totalCost=totalRevenue+timeDollarValue;
  let grade,gradeLabel,gradeColor;
  if(totalCost<5000){grade="A";gradeLabel="Well-Optimized";gradeColor="#059669";}
  else if(totalCost<15000){grade="B";gradeLabel="Room to Grow";gradeColor="#2563EB";}
  else if(totalCost<30000){grade="C";gradeLabel="Significant Leakage";gradeColor="#D97706";}
  else if(totalCost<50000){grade="D";gradeLabel="Critical Leakage";gradeColor="#DC2626";}
  else{grade="F";gradeLabel="Emergency";gradeColor="#B91C1C";}
  return {deadLeadLeakage,postconsultLeakage,noshowLeakage,repLeakage,reactLeakage,totalRevenue,blendedHours,timeDollarValue,totalCost,grade,gradeLabel,gradeColor,apv,cons,brate};
}

// ─── RECOMMENDATION ENGINE (unchanged logic) ──────────────────────────────────
function fmtD(n){return "$"+Math.round(n).toLocaleString();}
function fmtK(n){return n>=1000?"$"+(n/1000).toFixed(0)+"K":"$"+n;}

function buildRecommendations(answers,scores){
  const recs=[];
  const badResponse=["whenwecan","voicemail","notsure"].includes(answers.q4)||["sameday","nextday","noconsistent"].includes(answers.q5);
  const badFollowup=["ifweremember","ballisincourt","notsure"].includes(answers.q6);
  if(badResponse||badFollowup) recs.push({priority:scores.deadLeadLeakage,urgency:scores.deadLeadLeakage>5000?"critical":"high",leakage:scores.deadLeadLeakage,id:"lead_response",icon:"📵",title:"AI Missed Call & Lead Response",problem:"Leads are going cold before your practice responds. By the time someone follows up, they've already booked with a competitor who responded first.",what:"An AI-powered response layer that replies to every missed call and web inquiry within 90 seconds — 24 hours a day, 7 days a week. A 5-step follow-up sequence runs automatically over 14 days for anyone who doesn't book on first contact.",impact:`Recovering just 15% of currently lost leads adds approximately ${fmtD(scores.deadLeadLeakage*0.15)} per month.`,diy:{tools:"Twilio + Zapier + your existing scheduling software",note:"Requires technical setup and ongoing maintenance."},dfy:{timeline:"Live within 48 hours",included:["Missed call text-back (24/7)","Web form instant response","5-step lead nurture sequence","Booking link integration","Monthly performance report"]},callTalk:"Open with: 'What does a typical missed call look like in your practice right now?'"});
  if(["ifwehavetime","waitforthem","nothing"].includes(answers.q8)) recs.push({priority:scores.postconsultLeakage,urgency:scores.postconsultLeakage>8000?"critical":"high",leakage:scores.postconsultLeakage,id:"postconsult",icon:"🚶",title:"AI Post-Consult Nurture Sequence",problem:"40–70% of patients who consult never book on the day. Without structured follow-up, the vast majority never hear from you again.",what:"A 5-touch automated sequence over 7–14 days. Social proof, hesitation handling, availability nudge, and a direct booking link — written specifically for cosmetic surgery patient psychology.",impact:`Recovering 15–20% of unconverted consults adds ${fmtD(scores.postconsultLeakage*0.18)} per month.`,diy:{tools:"ActiveCampaign or Klaviyo + SimpleTexting",note:"Copy is critical — generic follow-ups won't convert for cosmetic surgery patients."},dfy:{timeline:"Live within 3–4 days",included:["5-touch email + SMS sequence","Cosmetic surgery specific copy","Booking link in every touch","Auto-stops when patient books","Response detection and escalation"]},callTalk:"Ask: 'How many consultations last month do you think are still out there deciding?'"});
  if(["manualconfirm","relyonpatient","noreminders"].includes(answers.q9)) recs.push({priority:scores.noshowLeakage,urgency:scores.noshowLeakage>3000?"high":"medium",leakage:scores.noshowLeakage,id:"reminders",icon:"📅",title:"Automated Appointment Reminder Sequence",problem:answers.q9==="noreminders"?"No reminder system means your no-show rate is likely 20–28% — one of the highest avoidable revenue losses in any medical practice.":"A single confirmation email isn't enough. Without SMS reminders, a meaningful percentage of patients simply forget.",what:"48-hour SMS reminder with confirm/reschedule link. 2-hour reminder. 30-minute gentle reminder. Cancelled slots immediately made available for rebooking.",impact:`Reducing your no-show rate to under 5% recovers approximately ${fmtD(scores.noshowLeakage*0.75)} per month.`,diy:{tools:"Your existing EMR may already support this — check before building separately",note:"This is the one automation that might already exist in your scheduling software."},dfy:{timeline:"Live within 24 hours",included:["48hr + 2hr + 30min SMS reminders","One-tap confirm/reschedule link","Auto-fill cancelled slots","No-show rate tracking"]},callTalk:"Ask: 'What does your current no-show rate look like — and what happens when someone doesn't show?'"});
  const manualPreop=(answers.q10||[]).includes("preopinstructions")||(answers.q10||[]).includes("routineqa");
  if(manualPreop) recs.push({priority:scores.timeDollarValue*0.35,urgency:"medium",leakage:null,timeSaving:(TASK_HOURS.preopinstructions||0)+(TASK_HOURS.routineqa||0),id:"preoppostop",icon:"🏥",title:"AI Patient Education Sequences",problem:"Manually sending pre-op instructions and fielding repetitive post-op questions by phone is one of the biggest time drains in any surgical practice.",what:"Pre-op messages at 7, 3, and 1 day before surgery. Post-op check-ins at day 1, 3, 7, and 14. Every message personalised to procedure type.",impact:`Eliminates an estimated ${(TASK_HOURS.preopinstructions||0)+(TASK_HOURS.routineqa||0)} hours per month of manual communication.`,diy:{tools:"Mailchimp + SimpleTexting, or your EMR's messaging feature",note:"Content must be medically accurate and reviewed before sending."},dfy:{timeline:"Live within 5–7 days",included:["Pre-op sequences for top 3 procedure types","Post-op day 1/3/7/14 check-ins","Procedure-specific content","Patient satisfaction improvement"]},callTalk:"Ask: 'How many calls per week are just patients asking if what they're experiencing is normal?'"});
  const badReviews=["under25","25to75"].includes(answers.q12_count);
  const noReviewSystem=["sometimes","voluntary","dontask"].includes(answers.q12_method);
  if(badReviews||noReviewSystem) recs.push({priority:scores.repLeakage,urgency:answers.q12_count==="under25"?"critical":"high",leakage:scores.repLeakage,id:"reviews",icon:"⭐",title:"Automated Review Collection System",problem:answers.q12_count==="under25"?"With fewer than 25 Google reviews, your practice is essentially invisible in local search.":"Your review count is below the competitive threshold. Patients look for 75+ reviews before booking a high-consideration cosmetic procedure.",what:"Automated SMS sent 10 days after every procedure — timed to peak satisfaction. Negative sentiment is routed to a private feedback channel, protecting your public rating.",impact:`Practices with 100+ reviews generate an estimated ${fmtD(scores.repLeakage)} more per month in organic search leads.`,diy:{tools:"NiceJob or Grade.us — both have plastic surgery-specific templates",note:"One of the easier automations to implement yourself."},dfy:{timeline:"Live within 24–48 hours",included:["Post-procedure SMS trigger","Negative sentiment routing","Google review direct link","Monthly review velocity report","Competitor review tracking"]},callTalk:"Ask: 'What do you think a patient sees when they search for a plastic surgeon in your city?'"});
  if(["dontreach","nosystem"].includes(answers.q13)) recs.push({priority:scores.reactLeakage,urgency:"medium",leakage:scores.reactLeakage,id:"reactivation",icon:"💤",title:"AI Patient Reactivation Campaign",problem:"Your existing patient list is your most underutilised asset. Without a reactivation system, past patients drift toward competitors who stay in front of them.",what:"A quarterly AI-driven reactivation sequence segmented by procedure type and time since last visit. Engaged patients are automatically routed into a booking flow.",impact:`Industry data shows 85% of aesthetics patients purchase again when re-engaged — adding ${fmtD(scores.reactLeakage)} or more per month.`,diy:{tools:"Mailchimp or Klaviyo for email segmentation",note:"Works well as a DIY project if you have someone who can write and a clean patient email list."},dfy:{timeline:"First campaign live within 7–10 days",included:["Full patient list segmentation","Quarterly campaign calendar","Procedure-specific content","Automatic booking flow for warm leads"]},callTalk:"Ask: 'How many patients are on your total list from the last 3–5 years? And how often do they hear from you?'"});
  if((answers.q10||[]).includes("leadtracking")) recs.push({priority:scores.timeDollarValue*0.25,urgency:"medium",leakage:null,timeSaving:TASK_HOURS.leadtracking,id:"leadtracking",icon:"📋",title:"Automated Lead Tracking & Pipeline",problem:"Tracking leads manually means leads fall through the cracks and you have no visibility into where each potential patient is in the journey.",what:"An automated pipeline that captures every inquiry from phone, web form, Instagram, and referral. Each lead tagged by source, procedure interest, and status automatically.",impact:`Eliminates ${TASK_HOURS.leadtracking} hours per month of manual tracking and ensures no lead is ever forgotten.`,diy:{tools:"HubSpot Free CRM or Notion CRM template — both work for a solo practice",note:"HubSpot's free tier is genuinely capable. Worth trying before paying for anything."},dfy:{timeline:"Live within 3–5 days",included:["Full lead pipeline setup","Source tagging and tracking","Automated follow-up triggers","Monthly conversion rate report"]},callTalk:"Ask: 'If I asked you right now how many active leads haven't booked yet — could you tell me in 30 seconds?'"});
  return recs.sort((a,b)=>b.priority-a.priority);
}

// ─── QUESTION DATA ─────────────────────────────────────────────────────────────
const SCREENS=[
  {id:0,section:"Practice Basics",sectionNum:"01",headline:"Let's start with your practice fundamentals.",sub:"These numbers drive every calculation in your assessment.",questions:[
    {id:"q1",type:"choice",question:"What type of procedures make up the majority of your practice?",options:[{value:"surgical",label:"Primarily surgical",sub:"Breast, body, face procedures"},{value:"mix",label:"Mix of surgical & non-surgical",sub:"Both OR and office procedures"},{value:"nonsurgical",label:"Primarily non-surgical",sub:"Injectables, skin treatments"},{value:"everything",label:"Equal mix of everything",sub:"Full-spectrum aesthetic practice"}]},
    {id:"q2",type:"choice",question:"How many consultations do you average per month?",options:[{value:"under5",label:"Fewer than 5"},{value:"5to10",label:"5 – 10"},{value:"11to20",label:"11 – 20"},{value:"21to40",label:"21 – 40"},{value:"over40",label:"40+"}]},
    {id:"q3",type:"choice",question:"What is your average procedure or treatment value?",options:[{value:"under1k",label:"Under $1,000"},{value:"1k3k",label:"$1,000 – $3,000"},{value:"3k6k",label:"$3,000 – $6,000"},{value:"6k10k",label:"$6,000 – $10,000"},{value:"over10k",label:"Over $10,000"}]},
  ]},
  {id:1,section:"Lead Response",sectionNum:"02",headline:"How does your practice handle new patient interest?",sub:"This is where most practices lose the most revenue — often without realising it.",questions:[
    {id:"q4",type:"choice",question:"When a potential patient calls and no one answers, what happens?",options:[{value:"autotext",label:"Automated text reply within minutes",sub:"They hear from you immediately, 24/7"},{value:"within1hr",label:"Someone calls back within the hour"},{value:"whenwecan",label:"We call back when we have time",sub:"Could be hours or the next day"},{value:"voicemail",label:"Voicemail — they have to call again",sub:"No outbound response from the practice"},{value:"notsure",label:"Honestly not sure what happens",sub:"No defined process in place"}]},
    {id:"q5",type:"choice",question:"How quickly does your practice respond to new patient inquiries?",options:[{value:"automated",label:"Within 5 minutes",sub:"Automated system handles it immediately"},{value:"within1hr",label:"Within 1 hour"},{value:"sameday",label:"Same business day"},{value:"nextday",label:"Next day or longer"},{value:"noconsistent",label:"No consistent process"}]},
    {id:"q6",type:"choice",question:"After someone reaches out, how many follow-up touches do they receive before booking?",options:[{value:"automated",label:"Automated multi-step sequence",sub:"Multiple touchpoints over 1–2 weeks"},{value:"manual2x",label:"Someone follows up manually 1–2 times"},{value:"ifweremember",label:"We follow up once if we remember"},{value:"ballisincourt",label:"We don't follow up — ball is in their court"},{value:"notsure",label:"No defined follow-up process"}]},
  ]},
  {id:2,section:"Consultation Pipeline",sectionNum:"03",headline:"What happens between consultation and booked procedure?",sub:"The gap between 'interested' and 'booked' is where practices bleed the most revenue.",questions:[
    {id:"q7",type:"choice",question:"What percentage of your consultations result in a booked procedure?",options:[{value:"over70",label:"Over 70%",sub:"Very strong conversion rate"},{value:"50to70",label:"50 – 70%"},{value:"30to50",label:"30 – 50%",sub:"Industry average range"},{value:"under30",label:"Under 30%"},{value:"donttrack",label:"I don't track this",sub:"No visibility into conversion rate"}]},
    {id:"q8",type:"choice",question:"When a patient consults but doesn't book that day, what happens next?",options:[{value:"automated",label:"Automated follow-up sequence fires",sub:"Multiple touchpoints over the next 1–2 weeks"},{value:"manualdays",label:"Someone manually follows up within a few days"},{value:"ifwehavetime",label:"We follow up if we have time"},{value:"waitforthem",label:"We wait for them to come back"},{value:"nothing",label:"Nothing — we move on"}]},
    {id:"q9",type:"choice",question:"How are appointment reminders currently sent?",options:[{value:"multitouch",label:"Automated SMS + email at multiple points",sub:"48hr, 2hr, and day-of reminders"},{value:"singleauto",label:"Single automated confirmation only"},{value:"manualconfirm",label:"Someone manually calls or texts"},{value:"relyonpatient",label:"We rely on patients to remember"},{value:"noreminders",label:"No reminder system"}]},
  ]},
  {id:3,section:"Time & Manual Work",sectionNum:"04",headline:"Which tasks is your team doing manually right now?",sub:"Select everything that currently requires a human to do it.",questions:[
    {id:"q10",type:"multiselect",question:"Select all manual tasks your practice currently handles:",options:[
      {value:"confirmcalls",label:"Calling or texting patients to confirm appointments",hrs:4},
      {value:"routineqa",label:"Answering routine pre-op / post-op questions by phone",hrs:5},
      {value:"reviewrequests",label:"Manually requesting Google reviews from patients",hrs:2},
      {value:"consultfollowup",label:"Following up with patients who didn't book after consult",hrs:5},
      {value:"preopinstructions",label:"Sending pre-op instructions individually to each patient",hrs:3},
      {value:"pastpatients",label:"Reaching out to past patients for re-engagement",hrs:3},
      {value:"leadtracking",label:"Tracking leads manually in a spreadsheet or notebook",hrs:4},
    ]},
    {id:"q11",type:"choice",question:"How many hours per week does your team spend on repetitive patient communication and admin?",options:[{value:"under2",label:"Under 2 hours"},{value:"2to5",label:"2 – 5 hours"},{value:"5to10",label:"5 – 10 hours"},{value:"10to20",label:"10 – 20 hours"},{value:"over20",label:"Over 20 hours"}]},
  ]},
  {id:4,section:"Reputation & Reactivation",sectionNum:"05",headline:"How visible is your practice — and how well do you retain patients?",sub:"Your online reputation and past patient relationships are significant untapped revenue sources.",questions:[
    {id:"q12_count",type:"choice",question:"How many Google reviews does your practice currently have?",options:[{value:"under25",label:"Fewer than 25",sub:"Effectively invisible in local search"},{value:"25to75",label:"25 – 75",sub:"Below competitive threshold"},{value:"75to150",label:"75 – 150",sub:"Approaching competitive"},{value:"150to300",label:"150 – 300"},{value:"over300",label:"300+",sub:"Market leader in review presence"}]},
    {id:"q12_method",type:"choice",question:"How do you currently collect Google reviews?",options:[{value:"automated",label:"Automated request after every procedure"},{value:"inperson",label:"We ask in person at follow-up visits"},{value:"sometimes",label:"We ask occasionally when we remember"},{value:"voluntary",label:"We rely on patients to leave them voluntarily"},{value:"dontask",label:"We don't actively collect reviews"}]},
    {id:"q13",type:"choice",question:"How do you stay in contact with past patients between their visits?",options:[{value:"automated",label:"Automated sequences — newsletters, offers, check-ins"},{value:"occasional",label:"Occasional email blasts when we think of it"},{value:"promotions",label:"We reach out when we have a promotion"},{value:"dontreach",label:"We don't proactively reach out",sub:"Reactive, not proactive"},{value:"nosystem",label:"No system in place at all"}]},
  ]},
  {id:5,section:"Final Question",sectionNum:"06",headline:"One last question before your results.",sub:"This helps us make your strategy call as useful as possible.",questions:[
    {id:"q_final",type:"textarea",question:"What is the single biggest operational frustration in your practice right now?",placeholder:"e.g. 'We constantly lose track of leads who seemed interested but never booked'..."},
  ]},
];

// ─── ANIMATED COUNTER ─────────────────────────────────────────────────────────
function AnimatedNumber({value,prefix="$",duration=900}){
  const [d,setD]=useState(0);
  const r=useRef(null);
  useEffect(()=>{
    const s=d,e=value,t0=performance.now();
    const step=(now)=>{
      const p=Math.min((now-t0)/duration,1),ease=1-Math.pow(1-p,3);
      setD(Math.round(s+(e-s)*ease));
      if(p<1) r.current=requestAnimationFrame(step);
    };
    r.current=requestAnimationFrame(step);
    return()=>cancelAnimationFrame(r.current);
  },[value]);
  return <span>{prefix}{d.toLocaleString()}</span>;
}

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  bg:      "#F0F4F8",
  surface: "#FFFFFF",
  border:  "#E2E8F0",
  borderHover: "#CBD5E1",
  teal:    "#0D9488",
  tealDark:"#0F766E",
  tealPale:"#F0FDFA",
  tealBorder:"#99F6E4",
  navy:    "#0F172A",
  dark:    "#1E293B",
  mid:     "#475569",
  muted:   "#94A3B8",
  light:   "#CBD5E1",
  red:     "#DC2626",
  redPale: "#FEF2F2",
  amber:   "#D97706",
  amberPale:"#FFFBEB",
  green:   "#059669",
  greenPale:"#ECFDF5",
  purple:  "#7C3AED",
  purplePale:"#F5F3FF",
  blue:    "#2563EB",
};

// ─── COMPONENTS ───────────────────────────────────────────────────────────────
function ProgressBar({current,total}){
  return(
    <div style={{width:"100%",height:3,background:T.border,borderRadius:2,overflow:"hidden"}}>
      <div style={{height:"100%",width:((current/total)*100)+"%",background:`linear-gradient(90deg,${T.teal},#34D399)`,borderRadius:2,transition:"width 0.5s cubic-bezier(0.4,0,0.2,1)"}}/>
    </div>
  );
}

function SectionPill({label}){
  return(
    <div style={{display:"inline-flex",alignItems:"center",gap:6,background:T.tealPale,border:`1px solid ${T.tealBorder}`,borderRadius:20,padding:"5px 14px",marginBottom:16}}>
      <div style={{width:6,height:6,borderRadius:"50%",background:T.teal}}/>
      <span style={{fontSize:12,fontWeight:600,color:T.tealDark,letterSpacing:"0.02em"}}>{label}</span>
    </div>
  );
}

function LeakageTicker({scores,screenIndex}){
  if(screenIndex===0) return null;
  const total=scores.totalCost||0;
  return(
    <div style={{background:"#FFF7ED",border:"1px solid #FED7AA",borderRadius:12,padding:"14px 18px",marginBottom:20}}>
      <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.12em",color:T.amber,marginBottom:6}}>ESTIMATED MONTHLY COST OF DOING NOTHING</div>
      <div style={{display:"flex",gap:20,alignItems:"flex-end",flexWrap:"wrap"}}>
        <div>
          <div style={{fontSize:28,fontWeight:800,color:T.amber,letterSpacing:"-0.02em",lineHeight:1}}>
            <AnimatedNumber value={total}/>
          </div>
          <div style={{fontSize:11,color:"#92400E",marginTop:2}}>combined monthly cost</div>
        </div>
        {scores.totalRevenue>0&&(
          <div style={{borderLeft:`1px solid #FED7AA`,paddingLeft:18}}>
            <div style={{fontSize:16,fontWeight:700,color:T.red}}><AnimatedNumber value={scores.totalRevenue}/></div>
            <div style={{fontSize:11,color:T.muted,marginTop:1}}>revenue leaking</div>
          </div>
        )}
        {scores.blendedHours>0&&(
          <div style={{borderLeft:`1px solid #FED7AA`,paddingLeft:18}}>
            <div style={{fontSize:16,fontWeight:700,color:T.purple}}>{scores.blendedHours} hrs</div>
            <div style={{fontSize:11,color:T.muted,marginTop:1}}>manual work/month</div>
          </div>
        )}
      </div>
    </div>
  );
}

function ChoiceOption({option,selected,onClick}){
  return(
    <button onClick={()=>onClick(option.value)} style={{
      width:"100%",textAlign:"left",padding:"13px 16px",
      background:selected?T.tealPale:T.surface,
      border:`1.5px solid ${selected?T.teal:T.border}`,
      borderRadius:10,cursor:"pointer",color:T.dark,
      transition:"all 0.15s",fontFamily:"inherit",
      boxShadow:selected?`0 0 0 3px rgba(13,148,136,0.08)`:"none",
      position:"relative",overflow:"hidden",
    }}>
      {selected&&<div style={{position:"absolute",top:0,left:0,bottom:0,width:3,background:T.teal,borderRadius:"10px 0 0 10px"}}/>}
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <div style={{width:18,height:18,borderRadius:"50%",flexShrink:0,
          border:`2px solid ${selected?T.teal:T.light}`,
          background:selected?T.teal:"transparent",
          display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s"}}>
          {selected&&<div style={{width:6,height:6,borderRadius:"50%",background:"#fff"}}/>}
        </div>
        <div>
          <div style={{fontSize:14,fontWeight:selected?600:400,color:selected?T.tealDark:T.dark,lineHeight:1.3}}>{option.label}</div>
          {option.sub&&<div style={{fontSize:12,color:T.muted,marginTop:2}}>{option.sub}</div>}
          {option.hrs&&<div style={{fontSize:12,color:selected?T.teal:T.muted,marginTop:2}}>~{option.hrs} hrs/month</div>}
        </div>
      </div>
    </button>
  );
}

function MultiSelect({question,answers,onToggle}){
  const selected=answers[question.id]||[];
  return(
    <div style={{display:"flex",flexDirection:"column",gap:8}}>
      <div style={{fontSize:16,fontWeight:600,color:T.navy,marginBottom:2,lineHeight:1.4}}>{question.question}</div>
      <div style={{fontSize:12,color:T.muted,marginBottom:6}}>Select all that apply</div>
      {question.options.map(opt=>{
        const on=selected.includes(opt.value);
        return(
          <button key={opt.value} onClick={()=>onToggle(question.id,opt.value)} style={{
            width:"100%",textAlign:"left",padding:"11px 14px",
            background:on?T.tealPale:T.surface,
            border:`1.5px solid ${on?T.teal:T.border}`,
            borderRadius:9,cursor:"pointer",color:T.dark,transition:"all 0.15s",fontFamily:"inherit",
          }}>
            <div style={{display:"flex",alignItems:"center",gap:11}}>
              <div style={{width:16,height:16,borderRadius:4,flexShrink:0,
                border:`2px solid ${on?T.teal:T.light}`,
                background:on?T.teal:"transparent",
                display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s"}}>
                {on&&<span style={{color:"#fff",fontSize:10,lineHeight:1,fontWeight:700}}>✓</span>}
              </div>
              <div>
                <div style={{fontSize:13,fontWeight:on?600:400,color:on?T.tealDark:T.dark}}>{opt.label}</div>
                {opt.hrs&&<div style={{fontSize:11,color:on?T.teal:T.muted,marginTop:1}}>~{opt.hrs} hrs/month</div>}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── RECOMMENDATION CARD ──────────────────────────────────────────────────────
function RecCard({rec,index,expanded,onToggle}){
  const uc={
    critical:{color:T.red,bg:T.redPale,border:"#FECACA",label:"Critical Priority"},
    high:{color:T.amber,bg:T.amberPale,border:"#FDE68A",label:"High Priority"},
    medium:{color:T.blue,bg:"#EFF6FF",border:"#BFDBFE",label:"Recommended"},
  }[rec.urgency]||{color:T.blue,bg:"#EFF6FF",border:"#BFDBFE",label:"Recommended"};

  return(
    <div style={{background:T.surface,border:`1px solid ${expanded?T.teal:T.border}`,borderRadius:12,overflow:"hidden",transition:"all 0.2s",boxShadow:expanded?"0 4px 20px rgba(13,148,136,0.08)":"0 1px 3px rgba(0,0,0,0.04)"}}>
      <button onClick={onToggle} style={{width:"100%",background:"none",border:"none",padding:"16px 18px",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
        <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
          <div style={{fontSize:20,lineHeight:1,flexShrink:0,marginTop:2}}>{rec.icon}</div>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5,flexWrap:"wrap"}}>
              <span style={{fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:uc.color,background:uc.bg,border:`1px solid ${uc.border}`,padding:"2px 8px",borderRadius:4}}>#{index+1} — {uc.label.toUpperCase()}</span>
            </div>
            <div style={{fontSize:14,fontWeight:600,color:T.navy,lineHeight:1.3,marginBottom:4}}>{rec.title}</div>
            <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
              {rec.leakage!=null&&<span style={{fontSize:12,color:uc.color,fontWeight:600}}>{fmtD(rec.leakage)}/mo leaking</span>}
              {rec.timeSaving&&<span style={{fontSize:12,color:T.purple,fontWeight:600}}>~{rec.timeSaving} hrs/mo saved</span>}
            </div>
          </div>
          <div style={{width:22,height:22,borderRadius:"50%",flexShrink:0,background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",color:T.muted,fontSize:10,transition:"transform 0.2s",transform:expanded?"rotate(180deg)":"none"}}>▼</div>
        </div>
      </button>

      {expanded&&(
        <div style={{padding:"0 18px 20px",borderTop:`1px solid ${T.border}`}}>
          <div style={{marginTop:16,marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:T.red,marginBottom:5}}>THE PROBLEM</div>
            <div style={{fontSize:13,color:T.mid,lineHeight:1.6}}>{rec.problem}</div>
          </div>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:T.teal,marginBottom:5}}>WHAT THIS AUTOMATION DOES</div>
            <div style={{fontSize:13,color:T.mid,lineHeight:1.6}}>{rec.what}</div>
          </div>
          <div style={{background:T.greenPale,border:"1px solid #A7F3D0",borderRadius:8,padding:"10px 14px",marginBottom:14}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:T.green,marginBottom:3}}>EXPECTED IMPACT</div>
            <div style={{fontSize:13,color:"#065F46",lineHeight:1.5}}>{rec.impact}</div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"12px 14px"}}>
              <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:T.muted,marginBottom:8}}>🔧 IF YOU BUILD IT YOURSELF</div>
              <div style={{marginBottom:8}}>
                <div style={{fontSize:10,color:T.muted,fontWeight:700,marginBottom:3}}>TOOLS REQUIRED</div>
                <div style={{fontSize:12,color:T.mid,lineHeight:1.5}}>{rec.diy.tools}</div>
              </div>
              <div style={{padding:"7px 10px",background:T.surface,borderRadius:6,borderLeft:`2px solid ${T.light}`}}>
                <div style={{fontSize:11,color:T.muted,lineHeight:1.5}}>{rec.diy.note}</div>
              </div>
            </div>
            <div style={{background:T.tealPale,border:`1px solid ${T.tealBorder}`,borderRadius:9,padding:"12px 14px"}}>
              <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:T.teal,marginBottom:8}}>⚡ DONE-FOR-YOU</div>
              <div style={{marginBottom:8}}>
                <div style={{fontSize:10,color:T.teal,fontWeight:700,marginBottom:3}}>TIMELINE</div>
                <div style={{fontSize:12,color:T.tealDark,fontWeight:600}}>{rec.dfy.timeline}</div>
              </div>
              <div style={{fontSize:10,color:T.teal,fontWeight:700,marginBottom:5}}>INCLUDES</div>
              {rec.dfy.included.map((item,i)=>(
                <div key={i} style={{display:"flex",gap:6,alignItems:"flex-start",marginBottom:4}}>
                  <span style={{color:T.teal,fontSize:10,marginTop:1,flexShrink:0}}>✓</span>
                  <span style={{fontSize:11,color:T.mid,lineHeight:1.4}}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── EMAIL GATE ───────────────────────────────────────────────────────────────
const FORMSPREE_URL = "https://formspree.io/f/mqenebyg";

function EmailGate({scores, answers, onUnlock}) {
  const [email, setEmail] = useState("");
  const [name, setName]   = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [focused, setFocused] = useState(null);

  async function handleSubmit() {
    if (!email || !email.includes("@")) { setError("Please enter a valid email address."); return; }
    setSubmitting(true);
    setError("");

    // Build a readable summary of their answers to send with the lead
    const recs = buildRecommendations(answers, scores);
    const recsummary = recs.map((r,i) =>
      `#${i+1} ${r.title}${r.leakage ? ` — ${fmtD(r.leakage)}/mo leaking` : r.timeSaving ? ` — ~${r.timeSaving} hrs/mo saved` : ""}`
    ).join(" | ");

    const summary = {
      email,
      name: name || "Not provided",
      grade: scores.grade,
      gradeLabel: scores.gradeLabel,
      totalMonthlyCost: fmtD(scores.totalCost),
      revenueLeaking: fmtD(scores.totalRevenue),
      annualLeakage: fmtD(scores.totalCost * 12),
      hoursLost: scores.blendedHours + " hrs/month",
      avgProcedureValue: fmtD(scores.apv),
      monthlyConsults: scores.cons,
      bookingRate: Math.round(scores.brate * 100) + "%",
      deadLeadLeakage: fmtD(scores.deadLeadLeakage),
      postconsultLeakage: fmtD(scores.postconsultLeakage),
      noshowLeakage: fmtD(scores.noshowLeakage),
      repLeakage: fmtD(scores.repLeakage),
      reactLeakage: fmtD(scores.reactLeakage),
      recommendedAutomations: recsummary,
      missedCallProcess: answers.q4 || "Not answered",
      responseTime: answers.q5 || "Not answered",
      postconsultFollowup: answers.q8 || "Not answered",
      reminderSystem: answers.q9 || "Not answered",
      googleReviews: answers.q12_count || "Not answered",
      pastPatientOutreach: answers.q13 || "Not answered",
      biggestFrustration: answers.q_final || "Not provided",
      submittedAt: new Date().toLocaleString(),
    };

    try {
      await fetch(FORMSPREE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(summary),
      });
      onUnlock(email);
    } catch(err) {
      setError("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  }

  const inputStyle = (field) => ({
    width: "100%", background: focused === field ? T.tealPale : T.bg,
    border: `1.5px solid ${focused === field ? T.teal : T.border}`,
    borderRadius: 9, padding: "12px 14px", color: T.dark, fontSize: 14,
    outline: "none", fontFamily: "inherit", transition: "all 0.15s",
    boxShadow: focused === field ? `0 0 0 3px rgba(13,148,136,0.08)` : "none",
  });

  return (
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:16,overflow:"hidden",boxShadow:"0 8px 32px rgba(0,0,0,0.08)",marginBottom:20}}>

      {/* Header */}
      <div style={{background:`linear-gradient(135deg,${T.teal},#0891B2)`,padding:"24px 28px",textAlign:"center"}}>
        <div style={{fontSize:11,fontWeight:700,letterSpacing:"0.12em",color:"rgba(255,255,255,0.75)",marginBottom:8}}>YOUR FULL REPORT IS READY</div>
        <div style={{fontSize:20,fontWeight:800,color:"#fff",lineHeight:1.3,marginBottom:6}}>
          Enter your email to unlock your personalised automation plan
        </div>
        <div style={{fontSize:13,color:"rgba(255,255,255,0.75)"}}>
          We'll also send you a copy so you can reference it before your strategy call
        </div>
      </div>

      <div style={{padding:"24px 28px"}}>
        {/* What they're unlocking */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:20}}>
          {[
            {icon:"💰", label:"Revenue leakage breakdown", sub:"By category with bar chart"},
            {icon:"🤖", label:"Personalised AI action plan", sub:"Ranked by impact on your numbers"},
            {icon:"🔧", label:"DIY vs done-for-you paths", sub:"For each automation"},
            {icon:"📅", label:"30-day implementation plan", sub:"What to build first"},
          ].map((item,i)=>(
            <div key={i} style={{display:"flex",gap:9,alignItems:"flex-start",padding:"10px 12px",background:T.bg,borderRadius:8,border:`1px solid ${T.border}`}}>
              <span style={{fontSize:16,flexShrink:0}}>{item.icon}</span>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:T.dark,lineHeight:1.3}}>{item.label}</div>
                <div style={{fontSize:11,color:T.muted,marginTop:2}}>{item.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Form */}
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div>
            <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.08em",color:T.muted,textTransform:"uppercase",display:"block",marginBottom:5}}>First Name</label>
            <input
              type="text" value={name} placeholder="Dr. Sarah"
              onChange={e=>setName(e.target.value)}
              onFocus={()=>setFocused("name")} onBlur={()=>setFocused(null)}
              style={inputStyle("name")}
            />
          </div>
          <div>
            <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.08em",color:T.muted,textTransform:"uppercase",display:"block",marginBottom:5}}>Email Address</label>
            <input
              type="email" value={email} placeholder="you@yourpractice.com"
              onChange={e=>setEmail(e.target.value)}
              onFocus={()=>setFocused("email")} onBlur={()=>setFocused(null)}
              onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
              style={inputStyle("email")}
            />
          </div>
          {error && <div style={{fontSize:12,color:T.red,background:T.redPale,padding:"8px 12px",borderRadius:7,border:`1px solid #FECACA`}}>{error}</div>}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              background:submitting?"#E2E8F0":`linear-gradient(135deg,${T.teal},#0891B2)`,
              color:submitting?T.muted:"#fff", border:"none", borderRadius:10,
              padding:"13px", fontSize:14, fontWeight:700, cursor:submitting?"not-allowed":"pointer",
              fontFamily:"inherit", boxShadow:submitting?"none":"0 4px 16px rgba(13,148,136,0.3)",
              transition:"all 0.15s", width:"100%",
            }}>
            {submitting ? "Sending..." : "Unlock My Full Report →"}
          </button>
          <div style={{fontSize:11,color:T.muted,textAlign:"center"}}>
            No spam. One email with your report, that's it.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── RESULTS PAGE ─────────────────────────────────────────────────────────────
function ResultsPage({scores,answers}){
  const [visible,setVisible]=useState(false);
  const [expanded,setExpanded]=useState(null);
  const [emailUnlocked,setEmailUnlocked]=useState(false);
  useEffect(()=>{setTimeout(()=>setVisible(true),100);},[]);
  const recs=buildRecommendations(answers,scores);
  const buckets=[
    {label:"Dead Lead Leakage",value:scores.deadLeadLeakage,color:T.red,bg:T.redPale,border:"#FECACA",icon:"📵",desc:"Revenue lost from slow or no response to new inquiries"},
    {label:"Unconverted Consult Loss",value:scores.postconsultLeakage,color:T.amber,bg:T.amberPale,border:"#FDE68A",icon:"🚶",desc:"Revenue from consultations that never became procedures"},
    {label:"No-Show Revenue Loss",value:scores.noshowLeakage,color:"#EA580C",bg:"#FFF7ED",border:"#FED7AA",icon:"❌",desc:"Procedures that never happened due to missed appointments"},
    {label:"Reputation Gap",value:scores.repLeakage,color:T.purple,bg:T.purplePale,border:"#DDD6FE",icon:"⭐",desc:"Estimated organic leads lost from weak review presence"},
    {label:"Dormant Patient Revenue",value:scores.reactLeakage,color:T.blue,bg:"#EFF6FF",border:"#BFDBFE",icon:"💤",desc:"Untapped revenue in your existing patient list"},
  ];
  const maxB=Math.max(...buckets.map(b=>b.value));

  return(
    <div style={{opacity:visible?1:0,transform:visible?"none":"translateY(12px)",transition:"all 0.5s ease"}}>

      {/* Grade Banner — always visible */}
      <div style={{background:T.surface,border:`2px solid ${scores.gradeColor}30`,borderRadius:16,padding:"24px 28px",marginBottom:20,boxShadow:"0 4px 24px rgba(0,0,0,0.06)"}}>
        <div style={{display:"flex",gap:24,alignItems:"center",flexWrap:"wrap"}}>
          <div style={{textAlign:"center",background:`${scores.gradeColor}10`,border:`2px solid ${scores.gradeColor}30`,borderRadius:12,padding:"16px 24px"}}>
            <div style={{fontSize:60,fontWeight:900,color:scores.gradeColor,lineHeight:1,letterSpacing:"-0.04em"}}>{scores.grade}</div>
            <div style={{fontSize:10,fontWeight:700,color:scores.gradeColor,letterSpacing:"0.08em",marginTop:4}}>{scores.gradeLabel.toUpperCase()}</div>
          </div>
          <div style={{flex:1,minWidth:180}}>
            <div style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",color:T.muted,marginBottom:5}}>TOTAL MONTHLY COST OF DOING NOTHING</div>
            <div style={{fontSize:38,fontWeight:900,color:T.amber,letterSpacing:"-0.03em",lineHeight:1}}>
              <AnimatedNumber value={scores.totalCost} duration={1200}/>
            </div>
            <div style={{fontSize:12,color:T.muted,marginTop:5}}>{fmtD(scores.totalRevenue)} revenue leaking + {fmtD(scores.timeDollarValue)} in manual admin time</div>
            <div style={{fontSize:12,color:T.muted,marginTop:3}}>That's <strong style={{color:T.amber}}>{fmtD(scores.totalCost*12)}</strong> per year at current trajectory</div>
          </div>
        </div>
      </div>

      {/* EMAIL GATE — shown until unlocked */}
      {!emailUnlocked ? (
        <EmailGate scores={scores} answers={answers} onUnlock={()=>setEmailUnlocked(true)}/>
      ) : (
        <>
      {/* Revenue Breakdown */}
      <div style={{marginBottom:20}}>
        <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.12em",color:T.muted,marginBottom:12}}>REVENUE LEAKAGE BREAKDOWN</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {buckets.map((b,i)=>(
            <div key={i} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 14px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:7}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:T.dark}}>{b.icon} {b.label}</div>
                  <div style={{fontSize:11,color:T.muted,marginTop:2}}>{b.desc}</div>
                </div>
                <div style={{fontSize:17,fontWeight:800,color:b.color,marginLeft:12,flexShrink:0}}>{fmtK(b.value)}<span style={{fontSize:10,color:T.muted,fontWeight:400}}>/mo</span></div>
              </div>
              <div style={{height:4,background:T.bg,borderRadius:4,overflow:"hidden"}}>
                <div style={{height:"100%",width:((b.value/maxB)*100)+"%",background:b.color,borderRadius:4,transition:"width 1s ease 0.3s",opacity:0.7}}/>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Time Suck */}
      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:"16px 18px",marginBottom:24,display:"flex",gap:18,alignItems:"center",flexWrap:"wrap",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
        <div style={{borderLeft:`3px solid ${T.purple}`,paddingLeft:14}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:T.purple,marginBottom:3}}>TIME SUCK SCORE</div>
          <div style={{fontSize:28,fontWeight:800,color:T.purple,lineHeight:1}}>{scores.blendedHours} <span style={{fontSize:14,fontWeight:500,color:T.mid}}>hrs/month</span></div>
          <div style={{fontSize:11,color:T.muted,marginTop:3}}>lost to manual tasks that should be automated</div>
        </div>
        <div style={{flex:1,minWidth:140,borderLeft:`1px solid ${T.border}`,paddingLeft:18}}>
          <div style={{fontSize:20,fontWeight:700,color:T.mid}}>{fmtD(scores.timeDollarValue)}</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>at $35/hr replacement cost</div>
          <div style={{fontSize:11,color:T.muted,marginTop:1}}>≈ <strong style={{color:T.purple}}>{Math.round(scores.blendedHours/4.3)} hrs/week</strong> of admin work</div>
        </div>
      </div>

      {/* Recommendations */}
      {recs.length>0&&(
        <div style={{marginBottom:24}}>
          <SectionPill label="Your Personalised Action Plan"/>
          <h2 style={{fontSize:22,fontWeight:700,color:T.navy,letterSpacing:"-0.02em",lineHeight:1.3,marginBottom:8,marginTop:0}}>
            {recs.length} automation{recs.length!==1?"s":""} that would close most of your leakage
          </h2>
          <p style={{fontSize:13,color:T.mid,lineHeight:1.6,marginBottom:16,marginTop:0}}>
            Each recommendation includes a DIY path and a done-for-you option. Ranked by impact on your specific numbers.
          </p>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {recs.map((rec,i)=>(
              <RecCard key={rec.id} rec={rec} index={i} expanded={expanded===rec.id} onToggle={()=>setExpanded(expanded===rec.id?null:rec.id)}/>
            ))}
          </div>
        </div>
      )}

      {/* Insights */}
      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:"16px 18px",marginBottom:20,boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
        <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.12em",color:T.muted,marginBottom:10}}>WHAT THIS MEANS FOR YOUR PRACTICE</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {scores.totalRevenue>20000&&(
            <div style={{display:"flex",gap:9,alignItems:"flex-start"}}>
              <div style={{width:5,height:5,borderRadius:"50%",background:T.red,marginTop:5,flexShrink:0}}/>
              <div style={{fontSize:13,color:T.mid,lineHeight:1.5}}>Recovering just 20% of current leakage adds <strong style={{color:T.navy}}>{fmtD(scores.totalRevenue*0.20)}/month</strong> without a single new marketing dollar.</div>
            </div>
          )}
          {scores.blendedHours>15&&(
            <div style={{display:"flex",gap:9,alignItems:"flex-start"}}>
              <div style={{width:5,height:5,borderRadius:"50%",background:T.purple,marginTop:5,flexShrink:0}}/>
              <div style={{fontSize:13,color:T.mid,lineHeight:1.5}}>Your team spends the equivalent of <strong style={{color:T.navy}}>{Math.round(scores.blendedHours/4.3)} hours per week</strong> on tasks AI can handle automatically.</div>
            </div>
          )}
          <div style={{display:"flex",gap:9,alignItems:"flex-start"}}>
            <div style={{width:5,height:5,borderRadius:"50%",background:T.teal,marginTop:5,flexShrink:0}}/>
            <div style={{fontSize:13,color:T.mid,lineHeight:1.5}}>The automations above can be running in your practice within <strong style={{color:T.navy}}>7 days</strong> — without changing your scheduling software or adding staff.</div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={{background:`linear-gradient(135deg,${T.teal},#0891B2)`,borderRadius:14,padding:"26px 28px",textAlign:"center",boxShadow:"0 12px 40px rgba(13,148,136,0.25)"}}>
        <SectionPill label="Your Next Step"/>
        <div style={{fontSize:22,fontWeight:700,color:"#fff",marginBottom:8,lineHeight:1.3}}>Book your free 30-minute strategy call</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",marginBottom:20,lineHeight:1.6}}>
          We'll build a full personalised report from your answers, walk through which automations to implement first, and give you a clear 30-day plan — whether you work with us or not.
        </div>
        <button
          style={{background:"#fff",color:T.tealDark,border:"none",borderRadius:10,padding:"14px 32px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 16px rgba(0,0,0,0.15)",letterSpacing:"-0.01em",transition:"all 0.15s"}}
          onMouseEnter={e=>{e.target.style.transform="translateY(-1px)";e.target.style.boxShadow="0 6px 20px rgba(0,0,0,0.2)";}}
          onMouseLeave={e=>{e.target.style.transform="none";e.target.style.boxShadow="0 4px 16px rgba(0,0,0,0.15)";}}
          onClick={()=>window.open("https://calendly.com/ndelorenzo30/ai-implementation-consult","_blank")}
        >
          Book My Strategy Call →
        </button>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",marginTop:10}}>No pitch. No pressure. Just your numbers and a clear plan.</div>
      </div>

      <div style={{textAlign:"center",marginTop:20,fontSize:11,color:T.muted}}>
        Assessment by <strong style={{color:T.mid}}>Service Solutions Pro</strong> · getservicesolutionspro.com
      </div>
        </>
      )}
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App(){
  const [screenIndex,setScreenIndex]=useState(0);
  const [answers,setAnswers]=useState({});
  const [showResults,setShowResults]=useState(false);
  const [transitioning,setTransitioning]=useState(false);

  const scores=calcScores(answers);
  const currentScreen=SCREENS[screenIndex];
  const totalScreens=SCREENS.length;
  const questionsDone=SCREENS.slice(0,screenIndex).reduce((s,sc)=>s+sc.questions.length,0);
  const totalQuestions=SCREENS.reduce((s,sc)=>s+sc.questions.length,0);

  function setAnswer(qid,val){setAnswers(p=>({...p,[qid]:val}));}
  function toggleMulti(qid,val){setAnswers(p=>{const c=p[qid]||[];return{...p,[qid]:c.includes(val)?c.filter(v=>v!==val):[...c,val]};});}
  function screenComplete(){for(const q of currentScreen.questions){if(q.type==="choice"&&!answers[q.id])return false;}return true;}

  function advance(){
    if(screenIndex<totalScreens-1){
      setTransitioning(true);
      setTimeout(()=>{setScreenIndex(i=>i+1);setTransitioning(false);window.scrollTo({top:0,behavior:"smooth"});},180);
    } else {
      setTransitioning(true);
      setTimeout(()=>{setShowResults(true);setTransitioning(false);window.scrollTo({top:0,behavior:"smooth"});},180);
    }
  }
  function goBack(){if(screenIndex>0){setTransitioning(true);setTimeout(()=>{setScreenIndex(i=>i-1);setTransitioning(false);},150);}}
  const canAdvance=screenComplete();

  return(
    <div style={{minHeight:"100vh",background:T.bg,fontFamily:"'DM Sans','Segoe UI',sans-serif",color:T.dark,paddingBottom:60}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,400&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        input::placeholder,textarea::placeholder{color:${T.light};}
        textarea{resize:none;}
        @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}
        .fu{animation:fadeUp 0.3s ease forwards;}
        button:hover{opacity:0.92;}
        ::-webkit-scrollbar{width:5px;}
        ::-webkit-scrollbar-track{background:${T.bg};}
        ::-webkit-scrollbar-thumb{background:${T.light};border-radius:3px;}
      `}</style>

      {/* Top bar */}
      <div style={{borderBottom:`1px solid ${T.border}`,padding:"14px 24px",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,background:"rgba(240,244,248,0.95)",backdropFilter:"blur(12px)",zIndex:100}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{width:28,height:28,background:T.teal,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <span style={{color:"#fff",fontSize:14,lineHeight:1}}>✦</span>
          </div>
          <span style={{fontSize:14,fontWeight:700,color:T.navy,letterSpacing:"-0.01em"}}>Service<span style={{color:T.teal}}>Solutions</span>Pro</span>
        </div>
        {!showResults&&(
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:11,color:T.muted}}>{questionsDone} of {totalQuestions} questions</span>
            <div style={{width:80}}><ProgressBar current={questionsDone} total={totalQuestions}/></div>
          </div>
        )}
      </div>

      <div style={{maxWidth:640,margin:"0 auto",padding:"32px 18px"}}>
        {showResults?(
          <div className="fu">
            <div style={{marginBottom:24}}>
              <SectionPill label="Assessment Results"/>
              <h1 style={{fontSize:28,fontWeight:800,color:T.navy,letterSpacing:"-0.025em",lineHeight:1.2,marginTop:0}}>
                Here's where your practice<br/><span style={{color:scores.gradeColor}}>is losing money right now.</span>
              </h1>
            </div>
            <ResultsPage scores={scores} answers={answers}/>
          </div>
        ):(
          <div className="fu" key={screenIndex} style={{opacity:transitioning?0:1,transition:"opacity 0.18s ease"}}>
            <LeakageTicker scores={scores} screenIndex={screenIndex}/>

            {/* Section header */}
            <div style={{marginBottom:24}}>
              <SectionPill label={`${currentScreen.sectionNum} of ${String(totalScreens).padStart(2,"0")} — ${currentScreen.section}`}/>
              <h2 style={{fontSize:22,fontWeight:700,color:T.navy,letterSpacing:"-0.02em",lineHeight:1.3,marginBottom:6,marginTop:0}}>{currentScreen.headline}</h2>
              <p style={{fontSize:14,color:T.mid,lineHeight:1.6,margin:0}}>{currentScreen.sub}</p>
            </div>

            {/* Questions */}
            <div style={{display:"flex",flexDirection:"column",gap:24}}>
              {currentScreen.questions.map(q=>(
                <div key={q.id} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,padding:"20px 20px",boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
                  {q.type==="multiselect"?(
                    <MultiSelect question={q} answers={answers} onToggle={toggleMulti}/>
                  ):q.type==="textarea"?(
                    <div>
                      <div style={{fontSize:15,fontWeight:600,color:T.navy,marginBottom:12,lineHeight:1.4}}>{q.question}</div>
                      <textarea value={answers[q.id]||""} onChange={e=>setAnswer(q.id,e.target.value)}
                        placeholder={q.placeholder} rows={4}
                        style={{width:"100%",background:T.bg,border:`1.5px solid ${T.border}`,borderRadius:9,padding:"12px 14px",color:T.dark,fontSize:13,lineHeight:1.6,outline:"none",fontFamily:"inherit",transition:"border-color 0.15s"}}
                        onFocus={e=>e.target.style.borderColor=T.teal}
                        onBlur={e=>e.target.style.borderColor=T.border}
                      />
                      <div style={{fontSize:11,color:T.muted,marginTop:5}}>Optional — helps us tailor your strategy call</div>
                    </div>
                  ):(
                    <div>
                      <div style={{fontSize:15,fontWeight:600,color:T.navy,marginBottom:12,lineHeight:1.4}}>{q.question}</div>
                      <div style={{display:"flex",flexDirection:"column",gap:7}}>
                        {q.options.map(opt=>(
                          <ChoiceOption key={opt.value} option={opt} selected={answers[q.id]===opt.value} onClick={val=>setAnswer(q.id,val)}/>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Navigation */}
            <div style={{display:"flex",justifyContent:screenIndex>0?"space-between":"flex-end",marginTop:24,gap:10}}>
              {screenIndex>0&&(
                <button onClick={goBack} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:9,padding:"11px 20px",color:T.mid,cursor:"pointer",fontSize:13,fontWeight:500,transition:"all 0.15s",fontFamily:"inherit",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>← Back</button>
              )}
              <button onClick={advance} disabled={!canAdvance} style={{
                background:canAdvance?`linear-gradient(135deg,${T.teal},#0891B2)`:"#E2E8F0",
                border:"none",borderRadius:9,padding:"11px 26px",
                color:canAdvance?"#fff":T.muted,cursor:canAdvance?"pointer":"not-allowed",
                fontSize:13,fontWeight:700,transition:"all 0.15s",fontFamily:"inherit",
                boxShadow:canAdvance?"0 4px 16px rgba(13,148,136,0.3)":"none",letterSpacing:"-0.01em",
              }}>
                {screenIndex===totalScreens-1?"See My Results →":"Continue →"}
              </button>
            </div>

            {/* Screen dots */}
            <div style={{display:"flex",justifyContent:"center",gap:6,marginTop:20}}>
              {SCREENS.map((_,i)=>(
                <div key={i} style={{width:i===screenIndex?18:6,height:6,borderRadius:3,
                  background:i<screenIndex?T.teal:i===screenIndex?T.teal:T.border,
                  transition:"all 0.3s ease"}}/>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
